/**
 * article-faq.js
 * Auto-detects FAQ blocks inside #article-content.
 *
 * Detection strategy:
 *   1. If there is an h2/h3 whose text matches "faq" / "frequently asked",
 *      collect every h3/h4 after it (until the next h2 that isn't the FAQ header)
 *      as question nodes, each followed by its answer siblings.
 *   2. Fallback: any h3 or h4 anywhere in the content whose text ends with "?".
 *
 * Each detected question + its following sibling content (until the next
 * heading of the same or higher level) is converted into an accordion item.
 */

(function () {
  function buildFaqAccordion(questionNodes) {
    if (!questionNodes.length) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'article-faq';

    questionNodes.forEach(({ question, answerEls }, i) => {
      const itemId = `article-faq-${i}`;

      const item = document.createElement('div');
      item.className = 'article-faq-item';

      // Transfer any ID the TOC already assigned to this heading so TOC
      // anchor links still scroll to the accordion item after the heading
      // is removed from the DOM.
      if (question.id) item.id = question.id;

      const btn = document.createElement('button');
      btn.className = 'article-faq-question';
      btn.type = 'button';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', itemId);
      btn.innerHTML = `
        <span class="article-faq-question-text">${question.innerHTML}</span>
        <svg class="article-faq-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1L6 7L11 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;

      const answerWrap = document.createElement('div');
      answerWrap.className = 'article-faq-answer';
      answerWrap.id = itemId;
      answerWrap.setAttribute('aria-hidden', 'true');

      const answerInner = document.createElement('div');
      answerInner.className = 'article-faq-answer-inner';
      answerEls.forEach((el) => answerInner.appendChild(el.cloneNode(true)));

      answerWrap.appendChild(answerInner);

      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        answerWrap.setAttribute('aria-hidden', String(open));
      });

      item.appendChild(btn);
      item.appendChild(answerWrap);
      wrapper.appendChild(item);
    });

    return wrapper;
  }

  function isImageElement(el) {
    return (
      el.tagName === 'IMG' ||
      el.tagName === 'FIGURE' ||
      el.querySelector('img') !== null
    );
  }

  function collectAnswerSiblings(questionEl) {
    const answers = [];
    const level = parseInt(questionEl.tagName[1], 10);
    let sibling = questionEl.nextElementSibling;

    while (sibling) {
      const sibLevel = sibling.tagName.match(/^H([2-6])$/i);
      if (sibLevel && parseInt(sibLevel[1], 10) <= level) break;

      // Images/figures are only included if they carry the faq-image class
      if (isImageElement(sibling)) {
        if (sibling.classList.contains('faq-image')) {
          answers.push(sibling);
        }
        // Images without faq-image are skipped but do NOT stop collection
        sibling = sibling.nextElementSibling;
        continue;
      }

      answers.push(sibling);
      sibling = sibling.nextElementSibling;
    }
    return answers;
  }

  function detectFaqSection(content) {
    // Look for a heading that signals a FAQ section
    const allHeadings = Array.from(content.querySelectorAll('h2, h3, h4'));

    const faqHeader = allHeadings.find((h) =>
      /faq|frequently asked/i.test(h.textContent)
    );

    if (faqHeader) {
      const faqLevel = parseInt(faqHeader.tagName[1], 10);
      const questionLevel = faqLevel + 1; // questions are one level deeper
      const questions = [];
      let sibling = faqHeader.nextElementSibling;

      while (sibling) {
        const m = sibling.tagName.match(/^H([2-6])$/i);
        if (m) {
          const lvl = parseInt(m[1], 10);
          if (lvl <= faqLevel) break; // back to parent level — stop
          if (lvl === questionLevel || sibling.textContent.trim().endsWith('?')) {
            questions.push({ question: sibling, answerEls: collectAnswerSiblings(sibling) });
          }
        }
        sibling = sibling.nextElementSibling;
      }

      if (questions.length >= 2) {
        return { faqHeader, questions };
      }
    }

    return null;
  }

  function injectFaqSchema(questions) {
    const entities = questions.map(({ question, answerEls }) => {
      const answerHtml = answerEls
        .map((el) => el.outerHTML.trim())
        .filter(Boolean)
        .join('');
      return {
        '@type': 'Question',
        name: question.textContent.trim(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerHtml,
        },
      };
    });

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: entities,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }

  function init() {
    const content = document.getElementById('article-content');
    if (!content) return;

    const faqSection = detectFaqSection(content);
    if (!faqSection) return;

    const { faqHeader, questions } = faqSection;

    // Inject schema before DOM nodes are removed
    injectFaqSchema(questions);

    const accordion = buildFaqAccordion(questions);
    if (!accordion) return;

    questions.forEach(({ question, answerEls }) => {
      answerEls.forEach((el) => el.remove());
      question.remove();
    });

    faqHeader.insertAdjacentElement('afterend', accordion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
